import { Box } from '@mui/material';
import { latLng, LatLng } from 'leaflet';
import { FunctionComponent, useContext, useState } from 'react';
import { MapContainer, TileLayer, ZoomControl } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { AppContext } from '../../../core/config';
import { useBusinessContext } from '../../../core/hooks';
import { Loader } from '../../../ui/components';
import { L3ServiceLocationBusinessContextConfig } from '../../config/L3ServiceLocationBusinessContext.config';
import { useFilteredL3ServiceLocations } from '../../hooks';
import { createL3ServiceMarkerClusterIcon } from '../L3ServiceMarkerClusterIcon/L3ServiceMarkerClusterIcon';
import { TopologyOverlay } from '../TopologyOverlay/TopologyOverlay';
import { TopologySearchHandler } from '../TopologySearchHandler/TopologySearchHandler';
import { useL3ServiceLocationsApiPatch } from '../../hooks/L3ServiceLocationsApiPatch/L3ServiceLocationsApiPatch.hook';
import { L3ServiceLocation } from '../L3ServiceLocation/L3ServiceLocation';
import { GeographicCoordinates } from '../../model';

export enum TopologyOverlayLeftColumnView {
  None,
  ServicePositionChange
}

export const Topology: FunctionComponent = () => {
  useBusinessContext(L3ServiceLocationBusinessContextConfig);
  const { customerId } = useContext(AppContext);
  const {
    locations,
    isFilteringInProgress,
    isFilteringDone,
    isFetching,
    clearFilters,
    filters,
    updateFilters,
  } = useFilteredL3ServiceLocations(customerId);
  const { updateGeographicCoordinates } = useL3ServiceLocationsApiPatch(customerId);
  const [leftColumnView, setLeftColumnView] = useState<TopologyOverlayLeftColumnView>(TopologyOverlayLeftColumnView.None);
  const [selectedServiceItemId, setSelectedServiceItemId] = useState<number | null>(null);
  const [draggedItemId, setDraggedItemId] = useState<number | null>(null);
  const [editedL3ServiceCoordinates, setEditedL3ServiceCoordinates] = useState<LatLng>();
  const [originalL3ServiceCoordinates, setOriginalL3ServiceCoordinates] = useState<GeographicCoordinates>();
  const centerOfPoland = latLng(52, 19);
  const isReadyToBeDisplayed = !isFetching && !isFilteringInProgress && isFilteringDone;

  const saveNewPosition = (): void => {
    if (draggedItemId === undefined || draggedItemId === null || editedL3ServiceCoordinates === undefined) {
      console.warn(`Cannot update L3 service location position: itemId=${draggedItemId}, coordinates=${editedL3ServiceCoordinates}`);
    } else {
      updateGeographicCoordinates({
        itemId: draggedItemId,
        latitude: editedL3ServiceCoordinates.lat,
        longitude: editedL3ServiceCoordinates.lng
      });
    }
  };

  const restoreCoordinates = (): void => {
    const draggedLocation = locations?.find(location => location.itemId === draggedItemId);
    if (draggedLocation === undefined) {
      console.warn(`Cannot identify dragged L3 service location to restore its position`);
    } else if (originalL3ServiceCoordinates === undefined) {
      console.warn(`No coordinates stored to restore from`);
    } else {
      draggedLocation.coordinates.latitude = originalL3ServiceCoordinates.latitude;
      draggedLocation.coordinates.longitude = originalL3ServiceCoordinates.longitude;
    }
  };

  return <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
    { isReadyToBeDisplayed
    ? <>
        <TopologyOverlay
          locations={ locations }
          clearFilters={ clearFilters }
          filters={ filters }
          updateFilters={ updateFilters }
          leftColumnView={ leftColumnView }
          setLeftColumnView={ setLeftColumnView }
          setDraggedItemId={ setDraggedItemId }
          saveNewPosition={ saveNewPosition }
          restoreCoordinates={ restoreCoordinates }
        />
        <MapContainer
          center={ centerOfPoland }
          zoom={ 6 }
          scrollWheelZoom={ true }
          zoomControl={ false }
          style={{ width: '100%', height: '100%' }}
        >
          <TileLayer
            attribution='<a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
          />
          { locations !== null && <TopologySearchHandler locations={ locations } /> }
          <ZoomControl position='bottomright' />
          <MarkerClusterGroup
            chunkedLoading
            iconCreateFunction={ createL3ServiceMarkerClusterIcon }
          >
          { locations !== null && locations.map(location =>
            <L3ServiceLocation
              key={ location.itemId }
              editedServiceItemId={ selectedServiceItemId }
              draggedItemId={ draggedItemId }
              leftColumnView={ leftColumnView }
              location={ location }
              setDraggedL3ServiceCoordinates={ setEditedL3ServiceCoordinates }
              setEditedServiceItemId={ setSelectedServiceItemId }
              setDraggedItemId={ setDraggedItemId }
              setLeftColumnView={ setLeftColumnView }
              setOriginalCoordinates={ setOriginalL3ServiceCoordinates }
            />)
          }
          </MarkerClusterGroup>
        </MapContainer>
      </>
    : <Loader loading={true} />
    }
  </Box>;
};